import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRightIcon, ClockIcon, MapPinIcon, PhoneIcon } from 'lucide-react';
import { bookingItem, business } from '../../data/site';
const ease = [0.23, 1, 0.32, 1] as const;
const headline = [{
  text: 'La vapeur',
  accent: false
}, {
  text: 'qui redonne',
  accent: false
}, {
  text: 'vie à votre',
  accent: false
}, {
  text: 'voiture.',
  accent: true
}];
export function Hero() {
  const ref = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start start', 'end start']
  });
  const imageY = useTransform(scrollYProgress, [0, 1], ['0%', '18%']);
  const imageScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.15]);
  return <section ref={ref} className="relative isolate flex min-h-[100svh] w-full flex-col overflow-hidden bg-ink text-white" data-id="element-50" data-mp="1v4n9euz54762">
      <motion.img src="https://cdn.magicpatterns.com/patterns/generated-images/646b9dce-e5bc-43bd-9c4e-0aa704ea8424.jpg" alt="" aria-hidden="true" style={{
      y: imageY,
      scale: imageScale
    }} className="absolute inset-0 -z-10 h-full w-full object-cover object-[70%_center]" data-id="element-51" data-mp="1q6lm1xuollos" />
      <div className="absolute inset-0 -z-10 bg-ink/45 lg:bg-ink/25" aria-hidden="true" data-id="element-52" data-mp="1czzmbpoplrbb" />
      <div className="absolute inset-y-0 left-0 -z-10 w-full bg-ink/40 lg:w-1/2" aria-hidden="true" data-id="element-53" data-mp="1hxriwx0au1lu" />

      <div className="mx-auto flex w-full max-w-7xl flex-1 flex-col justify-end px-5 pb-10 pt-32 sm:px-8 lg:pb-14" data-id="element-54" data-mp="25nlwxhwctw7o">
        <h1 className="font-display text-[clamp(3rem,9vw,8.5rem)] font-bold leading-[0.92] tracking-[-0.035em]" data-id="element-55" data-mp="2anricgc16mhb">
          {headline.map((line, i) => <span key={line.text} className="block overflow-hidden pb-[0.06em]" data-id="element-56" data-mp="20v2advfovqqq" data-mpi={i}>
              <motion.span className={`block ${line.accent ? 'text-lime' : ''}`} initial={{
            y: '105%'
          }} animate={{
            y: '0%'
          }} transition={{
            duration: 0.3,
            delay: 0.1 + i * 0.06,
            ease
          }} data-id="element-57" data-mp="2olg37o63ltmd">
                {line.text}
              </motion.span>
            </span>)}
        </h1>

        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        delay: 0.4,
        ease
      }} className="mt-8 flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between" data-id="element-58" data-mp="2y7hdn9vom016">
          <p className="max-w-xl text-lg leading-relaxed text-white/80" data-id="element-59" data-mp="2kb3ulwbi1qkl">
            Nettoyage de véhicules à domicile dans le sud de la Somme. Jusqu'à <strong className="font-semibold text-white" data-id="element-60" data-mp="2ajpp97wv9wjj">99,9 % des bactéries éliminées</strong>, avec des produits faits maison, 100 % naturels et non allergisants.
          </p>
          <div className="flex flex-wrap gap-3" data-id="element-61" data-mp="26g1gilwi0uwp">
            <Link to={bookingItem.to} className="group inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-lime px-7 py-4 font-semibold text-ink transition-[transform,background-color] duration-150 ease-out hover:bg-white active:scale-[0.97]" data-id="element-62" data-mp="21jb2030ce7de">
              Réserver un nettoyage
              <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-0.5" aria-hidden="true" data-id="element-63" data-mp="gz5vkpg3sd4g" />
            </Link>
            <a href={business.phoneHref} className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-white/25 px-7 py-4 font-semibold text-white backdrop-blur-sm transition-[border-color,background-color] duration-150 ease-out hover:border-white hover:bg-white/10" data-id="element-64" data-mp="q1yosy8uiywx">
              <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-65" data-mp="2ajkizxie7j1" />
              {business.phone}
            </a>
          </div>
        </motion.div>

        <motion.dl initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.3,
        delay: 0.55,
        ease
      }} className="mt-12 grid gap-6 border-t border-white/15 pt-6 text-sm sm:grid-cols-3" data-id="element-66" data-mp="36qkkaesam6jn">
          <div className="flex gap-3" data-id="element-67" data-mp="2rnt0tsf43b62">
            <MapPinIcon className="mt-0.5 h-4 w-4 shrink-0 text-lime" aria-hidden="true" data-id="element-68" data-mp="2mogcf38ldw1d" />
            <div data-id="element-69" data-mp="31d43aw7hthkz">
              <dt className="sr-only" data-id="element-70" data-mp="31dma2mrs7e75">Adresse</dt>
              <dd className="text-white/80" data-id="element-71" data-mp="31ef72w0xupc3">
                {business.addressLine1}, {business.addressLine2}
                <span className="block text-white/50" data-id="element-72" data-mp="2wkb1ag3esncl">Intervention à domicile</span>
              </dd>
            </div>
          </div>
          <div className="flex gap-3" data-id="element-73" data-mp="35f7hhsljr09y">
            <ClockIcon className="mt-0.5 h-4 w-4 shrink-0 text-lime" aria-hidden="true" data-id="element-74" data-mp="35e661n0mcbrp" />
            <div data-id="element-75" data-mp="3kanh0d4wbodq">
              <dt className="sr-only" data-id="element-76" data-mp="3kb6yjsqmwar8">Horaires</dt>
              {business.hours.map(h => <dd key={h.days} className="text-white/80" data-id="element-77" data-mp="3fdezmerknodj">
                  <span className="text-white/50" data-id="element-78" data-mp="1px5pkf1de7df">{h.days}</span> · {h.time}
                </dd>)}
            </div>
          </div>
          <div className="flex gap-3" data-id="element-79" data-mp="1l0p11jq9dqzt">
            <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-lime" aria-hidden="true" data-id="element-80" data-mp="h2444hp07gq" />
            <div data-id="element-81" data-mp="5fn1zx9h2f20">
              <dt className="sr-only" data-id="element-82" data-mp="5g6bqqoyxx4y">Expérience</dt>
              <dd className="text-white/80" data-id="element-83" data-mp="5hzlwfysizhp">
                Depuis {business.since}
                <span className="block text-white/50" data-id="element-84" data-mp="3mw4nyif4sikc">Anciennement {business.formerName}</span>
              </dd>
            </div>
          </div>
        </motion.dl>
      </div>
    </section>;
}